/**
 * taskServiceFactory — the task Firestore layer, bound to a collection.
 *
 * WHY A FACTORY. Tasks now exist in more than one space (Work → `teacherTasks`,
 * Personal → `personalTasks`), and those are separate COLLECTIONS rather than a
 * `space` field on one — the collection is the wall that makes a private space
 * private (core.md: "a space boundary is a collection wall"). Threading a
 * collection argument through twenty-six exported functions would put the choice
 * at every call site, where getting it wrong once writes a household errand into
 * the shared studio queue. Bound once, a service holder cannot address another
 * space's data at all.
 *
 * A MOVE, NOT A REWRITE. Behaviour is unchanged from the single-collection
 * TaskService; `TaskService.js` binds Work and re-exports the identical public
 * API (same named exports, same default-export subset), so its five importers
 * are untouched.
 *
 * THREE COLLECTIONS, THREE DIFFERENT ANSWERS — the interesting part:
 *
 *   tasks       PER-SPACE. The wall.
 *
 *   categories  PER-SPACE, and Personal deliberately has NONE. `taskCategories`
 *               is gated isEmployeeOrAdmin(), so an owner could technically write
 *               to it — which is exactly the accident worth preventing: a
 *               household category would appear in the studio's category list.
 *               Under the user-defined property model a category is just a select
 *               property, so Personal is unlikely ever to need this collection.
 *               Unconfigured, the four category functions THROW rather than
 *               silently no-op, because a create that quietly does nothing is
 *               worse than one that says why.
 *
 *   reads       SHARED, on purpose. `taskReads` holds per-viewer "seen at"
 *               markers keyed `{uid}_{taskId}`, and its rule is self-scoped by
 *               uid — it never keys on a task's collection. Firestore auto-ids
 *               don't collide across collections, so one marker set serves every
 *               space, and lookups are by taskId. Splitting it would mean a
 *               second collection and a second rule to buy nothing.
 */

import { db } from '../firebase';
import {
	collection,
	doc,
	addDoc,
	updateDoc,
	deleteDoc,
	query,
	where,
	orderBy,
	getDocs,
	onSnapshot,
	serverTimestamp,
	setDoc,
	arrayUnion,
} from 'firebase/firestore';
// taskAssignees is the single source of truth for the name->UID map, shared
// verbatim with the Node migration scripts. Pure ESM named export, imported here
// directly (mixing module.exports into it makes webpack throw at bootstrap).
import { deriveAssigneeUids } from './taskAssignees';

/**
 * The three ways a non-admin teacher is ON a task, matching the rules'
 * isOnTask(): assigned (`assigneeUids`), @mentioned (`mentionedUids`), or the
 * one who created it (`createdBy`). Firestore allows one array-contains per
 * query and the rules must be provable per query, so these are three
 * listeners, not one OR query.
 */
export const VISIBLE_TASK_SCOPES = [
	['assigneeUids', 'array-contains'],
	['mentionedUids', 'array-contains'],
	['createdBy', '=='],
];

/**
 * Pure: union several listeners' rows by task id. A task that matches more
 * than one scope (assigned AND created by her) appears once.
 */
export const mergeTaskRows = (lists) => {
	const byId = new Map();
	for (const rows of lists) {
		for (const row of rows || []) {
			if (row && row.id && !byId.has(row.id)) byId.set(row.id, row);
		}
	}
	return [...byId.values()];
};

/**
 * Pure: the Auth UIDs a comment's mentions[] names. mentions[] holds canon
 * first names plus the literal 'claude' (the agent flag, not a person), and
 * both resolve through the same name->UID map the assignee field uses, so
 * 'claude' and any unknown name simply drop.
 */
export const mentionedUidsFrom = (mentions) =>
	deriveAssigneeUids((mentions || []).filter((m) => String(m).toLowerCase() !== 'claude'));

const DEFAULT_READS_COLLECTION = 'taskReads';

// Doc id is `{uid}_{taskId}` so the rule can be a pure self-scope check with no
// extra read (mirrors pushTokens).
const readMarkerId = (uid, taskId) => `${uid}_${taskId}`;

/**
 * Pure helper: how has this task moved since the viewer last looked?
 * Module-level and dependency-free — it touches no collection, so it is shared
 * across every binding rather than rebuilt per service.
 *
 * Never-read is deliberately NOT "changed". Every card would light up on first
 * use, which is noise, not signal — the point is to show movement against a
 * baseline, and an unread card has no baseline yet.
 */
export const taskChangeState = (task, marker) => {
	if (!task) return { changed: false, newComments: 0, everRead: false };
	if (!marker || !marker.readAt) {
		// KNOWN EDGE, accepted deliberately (Handoff B, 2026-08-21): a card the
		// viewer never opened shows no badge even when comments land on it —
		// including a mail-capture comment folded onto a thread-keyed card. 84 of
		// 236 open cards carried comments the day thread-keying shipped, so
		// counting from zero here would light a third of the board on first use;
		// urgent capture comments still push to the phone regardless.
		return { changed: false, newComments: 0, everRead: false };
	}
	// The movement clock prefers lastActivityAt (trigger-owned: stamped by
	// stampTaskActivity/stampCommentActivity on ANY real write, including the
	// proposedTriage merges that never bump updatedAt) and falls back to
	// updatedAt for docs the triggers haven't touched yet. Older docs simply
	// lack the field — absence is not staleness.
	const movedAt =
		task.lastActivityAt instanceof Date
			? task.lastActivityAt
			: task.updatedAt instanceof Date
			? task.updatedAt
			: null;
	const changed = !!movedAt && movedAt.getTime() > marker.readAt.getTime();
	const newComments = Math.max(0, (task.commentCount ?? 0) - (marker.commentCountAtRead ?? 0));
	return { changed: changed || newComments > 0, newComments, everRead: true };
};

/** Shared timestamp coercion — every task read maps the same five date fields. */
const mapTaskDoc = (d) => ({
	id: d.id,
	...d.data(),
	createdAt: d.data().createdAt?.toDate?.() || new Date(),
	updatedAt: d.data().updatedAt?.toDate?.() || new Date(),
	deadline: d.data().deadline?.toDate?.() || null,
	closedAt: d.data().closedAt?.toDate?.() || null,
	// Trigger-owned (stampTaskActivity/stampCommentActivity); null until the
	// triggers first touch a doc, and taskChangeState/sorting treat null as
	// "fall back to updatedAt", never as "stale".
	lastActivityAt: d.data().lastActivityAt?.toDate?.() || null,
});

/**
 * @param {object} opts
 * @param {string} opts.tasksCollection      Firestore collection holding the tasks
 * @param {string} opts.space                value stamped on `space` for new tasks
 * @param {string|null} [opts.categoriesCollection] omit for a space with no categories
 * @param {string} [opts.readsCollection]    shared read-marker collection
 */
export function createTaskService({
	tasksCollection,
	space,
	categoriesCollection = null,
	readsCollection = DEFAULT_READS_COLLECTION,
}) {
	if (!tasksCollection || !space) {
		// A service bound to undefined addresses a garbage path. For a private
		// space that is a leak shape rather than a crash, so it fails at
		// construction instead of at the first query.
		throw new Error('createTaskService requires { tasksCollection, space }');
	}

	const COLLECTION = tasksCollection;

	const requireCategories = () => {
		if (!categoriesCollection) {
			throw new Error(
				`Task categories are not configured for the '${space}' space. ` +
					'Under the property model a category is a select property; ' +
					'do not write into another space\'s category list.'
			);
		}
		return categoriesCollection;
	};

	// ==================== READ MARKERS ====================

	/**
	 * Subscribe to the caller's own task read markers.
	 * Returns a plain object keyed by taskId: { [taskId]: { readAt, commentCountAtRead } }.
	 * Query is `where uid == me`, matching the rule predicate exactly — rules are
	 * not filters, so the query has to assert what the rule asserts.
	 */
	const subscribeToMyTaskReads = (uid, callback, onError) => {
		if (!uid) {
			callback({});
			return () => {};
		}
		const q = query(collection(db, readsCollection), where('uid', '==', uid));
		return onSnapshot(
			q,
			(snapshot) => {
				const byTask = {};
				snapshot.docs.forEach((d) => {
					const v = d.data();
					if (!v.taskId) return;
					byTask[v.taskId] = {
						readAt: v.readAt?.toDate?.() || null,
						commentCountAtRead: v.commentCountAtRead ?? 0,
					};
				});
				callback(byTask);
			},
			(error) => {
				console.error('Error subscribing to task reads:', error);
				if (onError) onError(error);
			}
		);
	};

	/**
	 * Mark a task as seen by this user, as of now.
	 *
	 * Fire-and-forget: a lost marker just means the card reads as changed, which is
	 * the safe direction. Never let it break the surface that called it.
	 */
	const markTaskRead = async (uid, taskId, commentCount = 0) => {
		if (!uid || !taskId) return;
		try {
			await setDoc(
				doc(db, readsCollection, readMarkerId(uid, taskId)),
				{
					uid,
					taskId,
					readAt: serverTimestamp(),
					commentCountAtRead: commentCount ?? 0,
				},
				{ merge: true }
			);
		} catch (e) {
			console.warn('markTaskRead failed (non-fatal):', e?.message || e);
		}
	};

	// ==================== TASKS ====================

	/**
	 * Create a new task. See the original TaskService JSDoc for the full field
	 * list; the only per-space value here is `space`.
	 */
	const createTask = async (task) => {
		const assignedTo = task.assignedTo || []; // Display mirror (teacher name strings)
		// Canonical assignees (Auth UIDs): honor an explicit non-empty assigneeUids
		// if a caller provides one, otherwise derive from the assignedTo names using
		// the SAME shared name->UID map as the Phase 1 backfill.
		const assigneeUids =
			Array.isArray(task.assigneeUids) && task.assigneeUids.length
				? task.assigneeUids
				: deriveAssigneeUids(assignedTo);
		const docRef = await addDoc(collection(db, COLLECTION), {
			title: task.title || task.text || '', // Support both new and legacy format
			description: task.description || '',
			text: task.text || task.title || '', // Keep for backwards compatibility
			assignedTo,
			assigneeUids,
			category: task.category || null,
			studentId: task.studentId || null,
			studentName: task.studentName || null,
			status: 'pending', // 'pending' | 'in_progress' | 'completed'
			area: task.area || null,
			priority: task.priority || 'medium', // high|medium|low
			effort: task.effort || null, // small|medium|large
			source: task.source || 'manual', // manual|email|claude|notionAi|bills
			projectId: task.projectId || null,
			leadId: task.leadId || null,
			parentId: task.parentId || null,
			externalEventId: task.externalEventId || null,
			// Queue signal: area is the sole discriminator for a portal create — an
			// area set means the person already sorted it (triaged:true); a bare
			// capture (no area) still needs triage. Honor an explicit override.
			triaged: typeof task.triaged === 'boolean' ? task.triaged : Boolean(task.area),
			closedAt: null, // written by the stampClosedDate trigger only, never by hand
			space,
			createdAt: serverTimestamp(),
			createdBy: task.createdBy || null,
			createdByName: task.createdByName || null,
			updatedAt: serverTimestamp(),
			deadline: task.deadline || null,
			subtaskCount: 0,
			commentCount: 0,
		});
		return docRef.id;
	};

	const updateTask = async (taskId, updates) => {
		const taskRef = doc(db, COLLECTION, taskId);
		// closedAt is owned by the stampClosedDate Cloud Function trigger (status->completed
		// stamps it, off completed clears it). Never let a hand update overwrite it.
		const { closedAt, ...safeUpdates } = updates || {};
		await updateDoc(taskRef, {
			...safeUpdates,
			updatedAt: serverTimestamp(),
		});
	};

	const updateTaskStatus = async (taskId, status) => {
		await updateTask(taskId, { status });
	};

	const deleteTask = async (taskId) => {
		await deleteDoc(doc(db, COLLECTION, taskId));
	};

	const getTasksByStudent = async (studentId) => {
		const q = query(
			collection(db, COLLECTION),
			where('studentId', '==', studentId),
			orderBy('createdAt', 'desc')
		);
		const snapshot = await getDocs(q);
		return snapshot.docs.map(mapTaskDoc);
	};

	const getIncompleteTasks = async () => {
		const q = query(
			collection(db, COLLECTION),
			where('status', 'in', ['pending', 'in_progress']),
			orderBy('createdAt', 'desc')
		);
		const snapshot = await getDocs(q);
		return snapshot.docs.map(mapTaskDoc);
	};

	const getTasksByTeacher = async (teacherName) => {
		const q = query(
			collection(db, COLLECTION),
			where('assignedTo', 'array-contains', teacherName),
			orderBy('createdAt', 'desc')
		);
		const snapshot = await getDocs(q);
		return snapshot.docs.map(mapTaskDoc);
	};

	const subscribeToStudentTasks = (studentId, callback) => {
		const q = query(
			collection(db, COLLECTION),
			where('studentId', '==', studentId),
			orderBy('createdAt', 'desc')
		);
		return onSnapshot(
			q,
			(snapshot) => callback(snapshot.docs.map(mapTaskDoc)),
			(error) => {
				console.error('Error subscribing to student tasks:', error);
				// Still call callback with empty array so loading stops
				callback([]);
			}
		);
	};

	const subscribeToIncompleteTasks = (callback) => {
		const q = query(
			collection(db, COLLECTION),
			where('status', 'in', ['pending', 'in_progress']),
			orderBy('createdAt', 'desc')
		);
		return onSnapshot(q, (snapshot) => callback(snapshot.docs.map(mapTaskDoc)));
	};

	/**
	 * Subscribe to all tasks. For Work this is an admin/owner read: the Phase E1
	 * rules deny a non-admin's unscoped teacherTasks read, so without an error
	 * handler the listener never resolves and the consumer spins forever (#26).
	 * Callers a non-admin can reach MUST pass onError.
	 */
	const subscribeToAllTasks = (callback, onError) => {
		const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));
		return onSnapshot(
			q,
			(snapshot) => callback(snapshot.docs.map(mapTaskDoc)),
			(error) => {
				console.error('Error subscribing to all tasks:', error);
				if (onError) onError(error);
			}
		);
	};

	/**
	 * Tasks assigned to one user, scoped by canonical `assigneeUids`.
	 * Single-field `array-contains` (no orderBy → no composite index); the
	 * consumer does its own sorting.
	 */
	const subscribeToAssignedTasks = (uid, callback) => {
		if (!uid) {
			callback([]);
			return () => {};
		}
		const q = query(
			collection(db, COLLECTION),
			where('assigneeUids', 'array-contains', uid)
		);
		return onSnapshot(
			q,
			(snapshot) => callback(snapshot.docs.map(mapTaskDoc)),
			(error) => {
				console.error('Error subscribing to assigned tasks:', error);
				callback([]);
			}
		);
	};

	/**
	 * Every task a non-admin teacher is ON (see VISIBLE_TASK_SCOPES). Emits
	 * only once each listener has answered at least once, so the list does not
	 * flash "no tasks" and then fill in. A listener that errors counts as
	 * answered with nothing: the other two scopes still show, which is the
	 * right direction for a teacher (she loses a path, never the whole list).
	 */
	const subscribeToVisibleTasks = (uid, callback) => {
		if (!uid) {
			callback([]);
			return () => {};
		}
		const lists = VISIBLE_TASK_SCOPES.map(() => null);
		const emit = () => {
			if (lists.every((l) => l !== null)) callback(mergeTaskRows(lists));
		};
		const unsubs = VISIBLE_TASK_SCOPES.map(([field, op], i) =>
			onSnapshot(
				query(collection(db, COLLECTION), where(field, op, uid)),
				(snapshot) => {
					lists[i] = snapshot.docs.map(mapTaskDoc);
					emit();
				},
				(error) => {
					console.error(`Error subscribing to tasks by ${field}:`, error);
					lists[i] = [];
					emit();
				}
			)
		);
		return () => unsubs.forEach((u) => u());
	};

	// ==================== COMMENTS ====================

	const addTaskComment = async (taskId, comment) => {
		const commentsRef = collection(db, COLLECTION, taskId, 'comments');
		const docRef = await addDoc(commentsRef, {
			text: comment.text,
			authorName: comment.authorName || 'Unknown',
			authorId: comment.authorId || null,
			mentions: comment.mentions || [],
			// Provenance (canon source taxonomy): UI comments are 'manual'; agent
			// comments arrive via the MCP add_comment with source:'claude'. Older
			// comments have no source field — absence reads as human.
			source: comment.source || 'manual',
			createdAt: serverTimestamp(),
		});

		const taskRef = doc(db, COLLECTION, taskId);
		const commentSnapshot = await getDocs(query(collection(db, COLLECTION, taskId, 'comments')));
		// A person @mentioned here is added to the task's mentionedUids, which is
		// what lets a non-admin teacher open it (rules: isOnTask). Append-only,
		// like the thread: a later comment never un-mentions anyone.
		const mentionedUids = mentionedUidsFrom(comment.mentions);
		await updateDoc(taskRef, {
			commentCount: commentSnapshot.size,
			updatedAt: serverTimestamp(),
			...(mentionedUids.length ? { mentionedUids: arrayUnion(...mentionedUids) } : {}),
		});

		return docRef.id;
	};

	const deleteTaskComment = async (taskId, commentId) => {
		await deleteDoc(doc(db, COLLECTION, taskId, 'comments', commentId));

		const taskRef = doc(db, COLLECTION, taskId);
		const commentSnapshot = await getDocs(query(collection(db, COLLECTION, taskId, 'comments')));
		await updateDoc(taskRef, {
			commentCount: commentSnapshot.size,
			updatedAt: serverTimestamp(),
		});
	};

	const subscribeToTaskComments = (taskId, callback) => {
		const q = query(
			collection(db, COLLECTION, taskId, 'comments'),
			orderBy('createdAt', 'asc')
		);
		return onSnapshot(q, (snapshot) => {
			callback(
				snapshot.docs.map((d) => ({
					id: d.id,
					...d.data(),
					createdAt: d.data().createdAt?.toDate?.() || new Date(),
				}))
			);
		});
	};

	// ==================== SUBTASKS ====================

	const createSubtask = async (taskId, subtask) => {
		const subtasksRef = collection(db, COLLECTION, taskId, 'subtasks');
		const docRef = await addDoc(subtasksRef, {
			title: subtask.title || subtask.text || '',
			description: subtask.description || '',
			assignedTo: subtask.assignedTo || [],
			category: subtask.category || null,
			status: 'pending',
			createdAt: serverTimestamp(),
			createdBy: subtask.createdBy || null,
			updatedAt: serverTimestamp(),
			deadline: subtask.deadline || null,
		});

		const taskRef = doc(db, COLLECTION, taskId);
		const taskSnapshot = await getDocs(query(collection(db, COLLECTION, taskId, 'subtasks')));
		await updateDoc(taskRef, {
			subtaskCount: taskSnapshot.size,
			updatedAt: serverTimestamp(),
		});

		return docRef.id;
	};

	const updateSubtask = async (taskId, subtaskId, updates) => {
		await updateDoc(doc(db, COLLECTION, taskId, 'subtasks', subtaskId), {
			...updates,
			updatedAt: serverTimestamp(),
		});
	};

	const updateSubtaskStatus = async (taskId, subtaskId, status) => {
		await updateSubtask(taskId, subtaskId, { status });
	};

	const deleteSubtask = async (taskId, subtaskId) => {
		await deleteDoc(doc(db, COLLECTION, taskId, 'subtasks', subtaskId));

		const taskRef = doc(db, COLLECTION, taskId);
		const taskSnapshot = await getDocs(query(collection(db, COLLECTION, taskId, 'subtasks')));
		await updateDoc(taskRef, {
			subtaskCount: taskSnapshot.size,
			updatedAt: serverTimestamp(),
		});
	};

	const subscribeToSubtasks = (taskId, callback) => {
		const q = query(
			collection(db, COLLECTION, taskId, 'subtasks'),
			orderBy('createdAt', 'asc')
		);
		return onSnapshot(q, (snapshot) => callback(snapshot.docs.map(mapTaskDoc)));
	};

	// ==================== CATEGORIES ====================

	const createCategory = async (name, createdBy = null) => {
		const col = requireCategories();
		// Check if category already exists
		const existingQuery = query(
			collection(db, col),
			where('nameLower', '==', name.toLowerCase().trim())
		);
		const existing = await getDocs(existingQuery);
		if (!existing.empty) {
			return existing.docs[0].id; // Return existing category ID
		}

		const docRef = await addDoc(collection(db, col), {
			name: name.trim(),
			nameLower: name.toLowerCase().trim(),
			createdAt: serverTimestamp(),
			createdBy,
		});
		return docRef.id;
	};

	const getCategories = async () => {
		const col = requireCategories();
		const snapshot = await getDocs(query(collection(db, col), orderBy('name', 'asc')));
		return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
	};

	const subscribeToCategories = (callback) => {
		const col = requireCategories();
		const q = query(collection(db, col), orderBy('name', 'asc'));
		return onSnapshot(q, (snapshot) => {
			callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
		});
	};

	const deleteCategory = async (categoryId) => {
		const col = requireCategories();
		await deleteDoc(doc(db, col, categoryId));
	};

	return {
		// Introspection — lets a test or debug surface assert which space a
		// service instance addresses without reaching into Firestore.
		tasksCollection,
		space,
		categoriesCollection,

		subscribeToMyTaskReads,
		markTaskRead,
		createTask,
		updateTask,
		updateTaskStatus,
		deleteTask,
		getTasksByStudent,
		getIncompleteTasks,
		getTasksByTeacher,
		subscribeToStudentTasks,
		subscribeToIncompleteTasks,
		subscribeToAllTasks,
		subscribeToAssignedTasks,
		subscribeToVisibleTasks,
		addTaskComment,
		deleteTaskComment,
		subscribeToTaskComments,
		createSubtask,
		updateSubtask,
		updateSubtaskStatus,
		deleteSubtask,
		subscribeToSubtasks,
		createCategory,
		getCategories,
		subscribeToCategories,
		deleteCategory,
	};
}
